import { defineStore } from 'pinia'
import {
    ApiRes,
    BankAccount,
    FrontendBankAccountResource,
    getAccounts,
    getBankAccountsForLegalProfile,
    getKycDocumentsForLegalProfile,
    KycDocument,
    KycDocumentSaveRequestBody,
    postBatchKycDocuments,
    postKycDocument,
    putLegalProfile,
} from '../api'
import { NotificationType, notify } from '../useNotifications'
import { i18n } from '../../localization'
import { promiseHandler } from '../helpers'
import { useLegalAccountFormState } from './useAccountFormLegal'
import { isRequiredLegalProfileDocuments, requiredLegalProfileAttributesForInvestment } from '../../validation'
import { reactive } from 'vue'
import { App } from '../../typings/api-enums'
import { FinancialSituation } from './useAccountDataPersonal'
import { ComplianceProfile, EcspStatus } from '../../typings'
import {
    getLockableLegalProfileAttributes,
    getLockableLegalProfileRelations,
    getPlatformSettings,
} from '../usePlatformSettings'
import { LegalEsacComplianceProfile } from '../../components/wizard/ESAC/legal/form/forms'
import { isEsacLegalComplianceProfileLocked } from '../../components/account/legal/complianceProfile/esacComplianceProfile'

export interface LegalProfile {
    legal_profile_uid: string
    natural_profile_uid: string
    account_uid: string
    company_name: string
    description: string
    website: string
    registration_number: string
    tax_number: string
    address: string
    postal_code: string
    city: string
    country: string
    state: string
    iban: string
    bic: string
    created_on: string
    updated_at: string
    kyc_legal: boolean
    kyc_bank: boolean
    // accessors
    platform_has_payment_account: boolean
    // relations
    wallet: unknown
    beneficial_owners: unknown
    managers: unknown
    pending_invitations: unknown
    compliance_profiles: unknown
    ecsp_status: typeof EcspStatus | null
    membership_number: string | null
    membership_document: []
    membership_document_description: string | null
    depot_owner: string
    depot_number: string
    depot_bank: string
    depot_bic: string
}
export type LegalProfileRequestBody = LegalProfile

export class ComplianceProfiles {
    esac? = new LegalEsacComplianceProfile()
    ecsp? = new EcspComplianceProfile()
}

export class EcspComplianceProfile {
    investor_classification = new InvestorClassification()
    risk = new Risk()
    financial_situation = new FinancialSituation()
}

export class InvestorClassification {
    expert_investor_treatment: App.Enums.ComplianceProfile.YesNoOption = null
    self_disclosure: any | null = null
}

export class Risk {
    no_answer = false
    startup_investing: App.Enums.ComplianceProfile.ECSP.StartupInvestingAnswerOption = null
    investments_outside_stock_market: App.Enums.ComplianceProfile.ECSP.InvestmentsOutsideStockMarketAnswerOption = null
    subordinated_financial_instruments: App.Enums.ComplianceProfile.ECSP.SubordinatedFinancialInstrumentsAnswerOption =
        null
    profit_participation_certificate: App.Enums.ComplianceProfile.ECSP.ProfitParticipationCertificateAnswerOption = null
}

// the first argument is a unique id of the store across your application
export const useLegalAccountDataState = defineStore('legal-account-data', {
    state: () => {
        return {
            legalProfile: {
                legal_profile_uid: '',
                natural_profile_uid: '',
                account_uid: '',
                company_name: '',
                description: '',
                website: '',
                registration_number: '',
                legal_form: '',
                registration_authority: '',
                tax_number: '',
                address: '',
                postal_code: '',
                city: '',
                country: '',
                state: '',
                iban: '',
                bic: '',
                created_on: '',
                updated_at: '',
                kyc_legal: null,
                kyc_bank: null,
                is_owner: null,
                // accessors
                platform_has_payment_account: false,
                is_locked: false,
                // relations
                wallet: null,
                beneficial_owners: null,
                managers: null,
                pending_invitations: null,
                compliance_profiles: null as ComplianceProfiles | null,
                signatures: [],
                ecsp_status: null,
                company_email: '',
                depot_owner: '',
                depot_number: '',
                depot_bank: '',
                depot_bic: '',
            },
            loaded: false,
            accountId: null,
            loading: false,
            saving: false,
            kycDocuments: reactive([]),
            kycDocumentsLoading: false,
            bankAccountsLoading: false,
            bankAccounts: [] as FrontendBankAccountResource[],
        }
    },
    actions: {
        async fetchAccountData() {
            this.loading = true
            const { data } = await getAccounts()
            if (data.accounts.legal.length) {
                this.$patch({
                    legalProfile: applyComplianceProfileDefaults(data.legal_profile),
                    accountId: data.accounts.legal[0]?.account_uid,
                })
            }
            this.loading = false
            this.loaded = true
        },
        async fetchKycDocuments() {
            this.kycDocumentsLoading = true
            const response = await getKycDocumentsForLegalProfile(this.legalProfile.legal_profile_uid)
            this.kycDocumentsLoading = false

            this.$patch({ kycDocuments: response.data })
        },
        async fetchBankAccounts() {
            this.bankAccountsLoading = true
            const { data } = await getBankAccountsForLegalProfile(this.legalProfile.legal_profile_uid)
            this.bankAccountsLoading = false

            this.$patch({ bankAccounts: data })
        },
        async saveAccountData(): Promise<ApiRes> {
            console.log(this.legalProfile)
            return new Promise(async (resolve, reject) => {
                this.saving = true

                const legalProfileId = this.legalProfile.legal_profile_uid
                const [data, error] = await promiseHandler(putLegalProfile(legalProfileId, this.legalProfile))

                if (error) {
                    reject(error)
                } else {
                    notify(
                        'Success',
                        i18n.global.t('legal_account.saved_successfully_title'),
                        i18n.global.t('legal_account.saved_successfully_message')
                    )

                    const accountFormState = useLegalAccountFormState()
                    accountFormState.currentForm.resetForm({
                        values: accountFormState.currentForm.values,
                    })

                    resolve(data)
                }
                this.saving = false
            })
        },
        saveKycDocument(kycDocument: KycDocument) {
            const reqBody: KycDocumentSaveRequestBody = {
                ...kycDocument,
                legal_profile_uid: this.legalProfile.legal_profile_uid,
            }

            return new Promise((resolve, reject) => {
                postKycDocument(reqBody)
                    .then((data) => {
                        this.kycDocuments.push(data.data)
                        notify(
                            'Success' as NotificationType,
                            i18n.global.t('account.saved_successfully_title'),
                            i18n.global.t('account.saved_kyc_successfully_message')
                        )
                        resolve(data)
                    })
                    .catch(reject)
            })
        },
        async saveBatchKycDocuments(kycDocuments: KycDocument[]) {
            const reqBody: KycDocumentSaveRequestBody[] = []
            kycDocuments.map((kycDocument) => {
                reqBody.push(kycDocument)
            })

            const response = await postBatchKycDocuments(reqBody, this.legalProfile.legal_profile_uid)
        },
    },
    getters: {
        hasAllRequiredProfileAttributesForInvestment(): boolean {
            return (
                requiredLegalProfileAttributesForInvestment().filter((attribute) => {
                    return !this.legalProfile[attribute]
                }).length === 0
            )
        },
        isVerified(): boolean {
            return !(
                (getPlatformSettings().require_kyc_id_for_investments && !this.legalProfile.kyc_legal) ||
                (getPlatformSettings().require_kyc_bank_for_investments && !this.legalProfile.kyc_bank)
            )
        },
        getBankDataFromProfile(): BankAccount {
            return {
                holder: `${this.legalProfile.company_name}`,
                iban: this.legalProfile.iban,
            }
        },
    },
})

function applyComplianceProfileDefaults(legalAccount: any): any {
    const legalComplianceProfileDefaults: Record<ComplianceProfile, any> = {
        esac: new LegalEsacComplianceProfile(),
        ecsp: new EcspComplianceProfile(),
    }

    Object.keys(legalComplianceProfileDefaults).forEach((complianceProfile) => {
        if (
            legalAccount &&
            legalAccount.compliance_profiles &&
            getPlatformSettings().compliance_profile === complianceProfile
        ) {
            legalAccount.compliance_profiles = {
                [complianceProfile]: Object.assign(
                    legalComplianceProfileDefaults[complianceProfile],
                    legalAccount.compliance_profiles[complianceProfile]
                ),
            }
        }
    })

    return legalAccount
}

export function isLegalProfileAttributeLocked(attribute) {
    return (
        getPlatformSettings().compliance_profile === 'esac' &&
        isEsacLegalComplianceProfileLocked() &&
        getLockableLegalProfileAttributes().includes(attribute)
    )
}

export function beneficialOwnerEditingIsLocked() {
    return (
        getPlatformSettings().compliance_profile === 'esac' &&
        isEsacLegalComplianceProfileLocked() &&
        getLockableLegalProfileRelations().includes('beneficial_owners')
    )
}

export function isLegalComplianceProfileLocked() {
    return (
        getPlatformSettings().compliance_profile === 'esac' &&
        isEsacLegalComplianceProfileLocked() &&
        getLockableLegalProfileRelations().includes('compliance_profiles')
    )
}

export function managerEditingIsLocked() {
    return (
        getPlatformSettings().compliance_profile === 'esac' &&
        isEsacLegalComplianceProfileLocked() &&
        getLockableLegalProfileRelations().includes('natural_profiles')
    )
}

export function isLegalTaxResidenciesEditDisabled() {
    return (
        getPlatformSettings().compliance_profile === 'esac' &&
        isEsacLegalComplianceProfileLocked() &&
        getLockableLegalProfileRelations().includes('tax_residencies')
    )
}
